import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import * as A from './offerActions';
import type { Offer, PromoCode } from '../../components/offers/data/types';

function deferredPromise<T = void>(): {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (e: Error) => void;
} {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (e: Error) => void;
  const promise = new Promise<T>((res, rej) => { resolve = res; reject = rej; });
  return { promise, resolve, reject };
}

export function useOfferManagement() {
  const dispatch = useAppDispatch();
  const slice = useAppSelector((s) => s.offerManagement);

  const fetchOffers = useCallback(
    (locationId: string, token?: string | null, options?: { force?: boolean; skipDuplicateMs?: number }) => {
      const d = deferredPromise();
      dispatch(A.fetchOffersRequest({ locationId, token, options, deferred: { resolve: d.resolve, reject: d.reject } }));
      return d.promise;
    },
    [dispatch],
  );

  const setEditingOfferId = useCallback(
    (id: string | null) => {
      dispatch(A.offersSetEditing(id));
    },
    [dispatch],
  );

  const fetchOfferByIdOnServer = useCallback(
    async (offerId: string, locationId: string, token?: string | null): Promise<Offer> => {
      const d = deferredPromise<Offer>();
      dispatch(A.fetchOfferByIdRequest({ offerId, locationId, token, deferred: { resolve: d.resolve, reject: d.reject } }));
      return d.promise;
    },
    [dispatch],
  );

  const createOfferOnServer = useCallback(
    async (payload: Record<string, unknown>, token?: string | null): Promise<Offer> => {
      const d = deferredPromise<Offer>();
      dispatch(A.createOfferRequest({ data: payload as any, token, deferred: { resolve: d.resolve, reject: d.reject } }));
      return d.promise;
    },
    [dispatch],
  );

  const updateOfferOnServer = useCallback(
    async (
      offerId: string,
      locationId: string,
      payload: Record<string, unknown>,
      token?: string | null,
    ): Promise<void> => {
      const d = deferredPromise();
      dispatch(A.updateOfferRequest({
        data: { offerId, locationId, data: payload as any },
        token,
        deferred: { resolve: d.resolve, reject: d.reject },
      }));
      return d.promise;
    },
    [dispatch],
  );

  const deleteOfferOnServer = useCallback(
    async (offerId: string, locationId: string, token?: string | null): Promise<void> => {
      const d = deferredPromise();
      dispatch(A.deleteOfferRequest({ offerId, locationId, token, deferred: { resolve: d.resolve, reject: d.reject } }));
      return d.promise;
    },
    [dispatch],
  );

  const toggleOfferStatusOnServer = useCallback(
    async (offerId: string, locationId: string, status: Offer['status'], token?: string | null): Promise<void> => {
      const d = deferredPromise();
      dispatch(A.toggleOfferStatusRequest({
        data: { offerId, locationId, status },
        token,
        deferred: { resolve: d.resolve, reject: d.reject },
      }));
      return d.promise;
    },
    [dispatch],
  );

  const bulkDeleteOffersOnServer = useCallback(
    async (offerIds: string[], locationId: string, token?: string | null): Promise<void> => {
      const d = deferredPromise();
      dispatch(A.bulkDeleteOffersRequest({
        data: { offerIds, locationId },
        token,
        deferred: { resolve: d.resolve, reject: d.reject },
      }));
      return d.promise;
    },
    [dispatch],
  );

  const bulkArchiveOffersOnServer = useCallback(
    async (offerIds: string[], token?: string | null): Promise<void> => {
      const d = deferredPromise();
      dispatch(A.bulkArchiveOffersRequest({
        data: { offerIds },
        token,
        deferred: { resolve: d.resolve, reject: d.reject },
      }));
      return d.promise;
    },
    [dispatch],
  );

  const createPromoCodeOnServer = useCallback(
    async (
      offerId: string,
      locationId: string,
      codeData: Record<string, unknown>,
      token?: string | null,
    ): Promise<PromoCode> => {
      const d = deferredPromise<PromoCode>();
      dispatch(A.createPromoCodeRequest({
        data: { offerId, locationId, data: codeData as any },
        token,
        deferred: { resolve: d.resolve, reject: d.reject },
      }));
      return d.promise;
    },
    [dispatch],
  );

  return {
    ...slice,
    setEditingOfferId,
    fetchOffers,
    fetchOfferByIdOnServer,
    createOfferOnServer,
    updateOfferOnServer,
    deleteOfferOnServer,
    bulkDeleteOffersOnServer,
    toggleOfferStatusOnServer,
    bulkArchiveOffersOnServer,
    createPromoCodeOnServer,
  };
}

export function useOfferToastStore() {
  const dispatch = useAppDispatch();
  const slice = useAppSelector((s) => s.offerManagement.toast);
  const addToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      dispatch(A.toastAdd({ id, message, type }));
      setTimeout(() => {
        dispatch(A.toastRemove(id));
      }, 3000);
    },
    [dispatch],
  );
  const removeToast = useCallback((id: string) => dispatch(A.toastRemove(id)), [dispatch]);
  return useMemo(() => ({ ...slice, addToast, removeToast }), [slice, addToast, removeToast]);
}
